/**
 * OfflineIndicator — shows a small pill at the top of the screen when the
 * browser reports the device is offline.
 *
 * Why this matters for NeuroWave: the audio engine is fully client-side and
 * keeps working without a network, but features that talk to the backend
 * (saving session history, AI session creator, sharing presets, payments)
 * silently fail if the user is offline. Surfacing the offline state both
 * (a) reassures the user that the playback they hear isn't fake, and (b)
 * sets expectations about what won't work until they reconnect.
 *
 * Visibility rules:
 * - Mounts once at the App level.
 * - Listens to the standard `online` / `offline` window events plus checks
 *   `navigator.onLine` on mount (handles SPA reloads where the user was
 *   already offline before the listeners attached).
 * - Auto-hides ~3s after coming back online with a green "back online"
 *   confirmation, then disappears entirely.
 *
 * Style: dark glass pill, fixed top-center, non-blocking. data-testid on
 * the root + state pill so the testing agent can verify both states.
 */
import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

export const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  );
  // Briefly flash a "back online" confirmation when reconnecting.
  const [recentlyReconnected, setRecentlyReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setRecentlyReconnected(true);
      const t = setTimeout(() => setRecentlyReconnected(false), 3000);
      return () => clearTimeout(t);
    };
    const handleOffline = () => {
      setIsOffline(true);
      setRecentlyReconnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline && !recentlyReconnected) return null;

  const offlineCls =
    'bg-amber-950/90 border-amber-500/40 text-amber-200';
  const onlineCls =
    'bg-emerald-950/90 border-emerald-500/40 text-emerald-200';

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
      data-testid="offline-indicator-root"
      aria-live="polite"
    >
      <div
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border backdrop-blur-md text-xs font-medium shadow-lg transition-all duration-300 ${
          isOffline ? offlineCls : onlineCls
        }`}
        data-testid={isOffline ? 'offline-indicator-offline' : 'offline-indicator-online'}
      >
        {isOffline ? (
          <>
            <WifiOff className="w-3.5 h-3.5" />
            <span>Offline · audio still works · sessions won&apos;t save</span>
          </>
        ) : (
          <>
            <Wifi className="w-3.5 h-3.5" />
            <span>Back online</span>
          </>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;
